import React, { useState } from "react";
import { useFetchData, useDeleteData } from 'services/queries';
import Box from '@mui/material/Box';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Link } from "react-router-dom";
import { BsPencilSquare, BsTrash, BsFillEyeFill } from "react-icons/bs";
import { GridActionsCellItem } from '@mui/x-data-grid';
import useRoleBasedColumns from 'hooks/useRoleBasedColumns';
import SimpleDeleteDialog from 'components/dialog/SimpleDeleteDialog';
import ToolbarCrud from 'components/toolbar/ToolbarCrud';
import { CATEGORY_CHOICES } from 'constants';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

const TemplateItemList = () => {
  const resource = 'additional-cost/template-items';
  const resource_name = 'Template Item';
  const queryClient = useQueryClient();
  
  const { data: data, error: errorData, isLoading: isLoadingData } = useFetchData(
    resource,
    true
  );
  
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  const deleteMutation = useDeleteData(resource, [resource], {
    showNotification: true,
  });
  
  // Convert CATEGORY_CHOICES array to object for easy lookup
  const categoryDisplay = CATEGORY_CHOICES.reduce((acc, choice) => {
    acc[choice.value] = choice.label;
    return acc;
  }, {});
  
  const handleDelete = () => {
    if (selectedItem) {
      deleteMutation.mutate(selectedItem, {
        onSuccess: () => {
          setOpenDeleteDialog(false);
          setSelectedItem(null);
          queryClient.invalidateQueries([resource]);
          toast.success('Template item deleted successfully');
        },
        onError: (error) => {
          toast.error('Failed to delete template item');
        }
      });
    }
  };

  const columnsData = [
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 120,
      editable: false,
      groupable: false,
      aggregable: false,
      visibleTo: ['Administrator'],
      getActions: (params) => {
        const { id } = params.row;
        return [
          <GridActionsCellItem
            icon={<BsFillEyeFill />}
            color="primary"
            label="View"
            component={Link}
            to={`/admin/settings/additional-costs/template/view/${id}`}
          />,
          <GridActionsCellItem
            icon={<BsPencilSquare />}
            color="primary"
            label="Edit"
            component={Link}
            to={`/admin/settings/additional-costs/template/edit/${id}`}
          />,
          <GridActionsCellItem
            color="primary"
            icon={<BsTrash />}
            label="Delete"
            onClick={() => {
              setSelectedItem(params.row.id);
              setOpenDeleteDialog(true);
            }}
          />,
        ];
      }
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 200,
      editable: false,
      groupable: true,
      type: 'string',
      renderCell: (params) => {
        if (!params.row) return '';
        // Use category_display from backend if available, otherwise use local mapping
        return params.row.category_display || categoryDisplay[params.row.category] || params.row.category;
      },
    },
    {
      field: 'subcategory_detail',
      headerName: 'Subcategory',
      width: 250,
      editable: false,
      groupable: false,
      type: 'string',
      renderCell: (params) => {
        if (!params.row || !params.row.subcategory_detail) return '-';
        return params.row.subcategory_detail.name;
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
      editable: false,
      groupable: false,
      type: 'string',
      renderCell: (params) => {
        return params.row?.description || '-';
      },
    },
    {
      field: 'amount',
      headerName: 'Amount ($)',
      width: 120,
      editable: false,
      groupable: false,
      type: 'number',
      renderCell: (params) => {
        if (!params.row?.amount) return '-';
        // Format as USD currency
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(params.row.amount);
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      editable: false,
      groupable: false,
      type: 'dateTime',
      valueGetter: (params) => {
        if (!params.row?.created_at) return null;
        return new Date(params.row.created_at);
      },
    },
  ];
  
  const columns = useRoleBasedColumns(columnsData);
  
  const rows = data || [];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGridPremium
          rows={rows}
          columns={columns}
          loading={isLoadingData}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pagination
          slots={{
            toolbar: ToolbarCrud,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              createLink: '/admin/settings/additional-costs/template/new',
              resource: resource_name,
            },
          }}
          checkboxSelection={false}
          disableRowSelectionOnClick
        />
      </Box>
      
      <SimpleDeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Delete Template Item"
        message="Are you sure you want to delete this template item? This will not affect existing orders."
      />
    </Box>
  );
};

export default TemplateItemList;